/**
 * The assessment framework: pillars, role levels and the maturity scale.
 *
 * Source of truth for the *content* (behavior names, descriptions, anchors) is
 * the database — it is editable, bilingual and versioned per organization.
 * What lives here is only the shape the content must fit into.
 */
/** The three pillars of the Novo Nordisk BASAC leadership model. */
export const PILLARS = ['DEBATE', 'DECIDE', 'DELIVER'];
/**
 * Role level of the *evaluated* person. It decides which set of anchors the
 * survey shows: the same behavior is described differently depending on
 * whether it is enacted on one's own work, through a team, or across an area.
 */
export const ROLE_LEVELS = ['SELF_LEADER', 'PEOPLE_LEADER', 'AREA_LEADER'];
/**
 * Maturity scale: 1 to 5. This is NOT a frequency scale — the rater's act is to
 * *place* the person on a ladder, not to rate how often something happens.
 */
export const MATURITY_STEPS = [1, 2, 3, 4, 5];
/**
 * Only steps 1, 3 and 5 carry a written anchor. Steps 2 and 4 are explicitly
 * "in transition" between two described steps and have no anchor of their own.
 */
export const ANCHORED_STEPS = [1, 3, 5];
/** Sentinel stored when the rater could not observe the behavior. */
export const NOT_OBSERVED = 'NA';
export const isAnchoredStep = (step) => ANCHORED_STEPS.includes(step);
export const isTransitionStep = (step) => step === 2 || step === 4;
/** The two anchored steps a transition step sits between (2 → 1|3, 4 → 3|5). */
export const transitionBetween = (step) => {
    if (step === 2)
        return [1, 3];
    if (step === 4)
        return [3, 5];
    return null;
};
/** Only maturity steps count as numeric; NOT_OBSERVED is excluded from every average. */
export const isScorable = (value) => value !== NOT_OBSERVED;
/** The canonical pillar of each behavior number (1-4 / 5-8 / 9-12). */
export const pillarForBehaviorNumber = (behaviorNumber) => {
    if (behaviorNumber <= 4)
        return 'DEBATE';
    if (behaviorNumber <= 8)
        return 'DECIDE';
    return 'DELIVER';
};
/**
 * The next described rung above a value.
 *
 * Only 1, 3 and 5 carry an anchor, so "what does better look like" can only
 * ever point at one of those. Somebody sitting at 3.0 is on Consistente, and
 * the rung ahead of them is Referente — there is nothing written in between.
 * Returns null once there is nothing above.
 */
export const nextAnchoredStep = (value) => {
    if (value < 3)
        return 3;
    if (value < 5)
        return 5;
    return null;
};
export const BEHAVIORS_PER_PILLAR = 4;
export const TOTAL_BEHAVIORS = PILLARS.length * BEHAVIORS_PER_PILLAR;
//# sourceMappingURL=framework.js.map